import { useState } from "react";
import { Link } from "react-router-dom";
import { Send, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import Reveal, { SectionHead } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", message: "", gdpr: false });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: key === "gdpr" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success(t.form.success);
      setForm({ name: "", business: "", email: "", phone: "", message: "", gdpr: false });
    } catch {
      toast.error(t.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32">
      <div className="pointer-events-none absolute top-1/4 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-blue-600/10 blur-[130px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <SectionHead
          badge={t.form.badge}
          title={t.form.title}
          sub={t.form.sub}
          testId="contact-title"
        />
        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="relative mx-auto mt-14 max-w-3xl overflow-hidden rounded-3xl border border-white/10 bg-[#0b0f19]/90 backdrop-blur-xl p-6 sm:p-10 shadow-[0_30px_90px_rgba(0,0,0,0.5)]"
          >
            <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-cyan-500/10 blur-[80px]" />
            <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cf-name" className="mb-2 block text-sm font-medium text-slate-300">{t.form.name} *</label>
                <input id="cf-name" data-testid="contact-input-name" required value={form.name} onChange={set("name")} className="field-input" />
              </div>
              <div>
                <label htmlFor="cf-business" className="mb-2 block text-sm font-medium text-slate-300">{t.form.business}</label>
                <input id="cf-business" data-testid="contact-input-business" value={form.business} onChange={set("business")} className="field-input" />
              </div>
              <div>
                <label htmlFor="cf-email" className="mb-2 block text-sm font-medium text-slate-300">{t.form.email} *</label>
                <input id="cf-email" data-testid="contact-input-email" type="email" required value={form.email} onChange={set("email")} className="field-input" />
              </div>
              <div>
                <label htmlFor="cf-phone" className="mb-2 block text-sm font-medium text-slate-300">{t.form.phone}</label>
                <input id="cf-phone" data-testid="contact-input-phone" type="tel" value={form.phone} onChange={set("phone")} className="field-input" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className="mb-2 block text-sm font-medium text-slate-300">{t.form.message}</label>
                <textarea
                  id="cf-message"
                  data-testid="contact-input-message"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  className="field-input min-h-32 py-3 resize-y"
                />
              </div>
              <label
                htmlFor="cf-gdpr"
                className="sm:col-span-2 flex min-h-12 cursor-pointer items-start gap-4 rounded-xl border border-white/10 bg-[#030712]/60 px-4 py-3 hover:border-cyan-400/30 transition-colors duration-300"
              >
                <input
                  id="cf-gdpr"
                  data-testid="contact-checkbox-gdpr"
                  type="checkbox"
                  required
                  checked={form.gdpr}
                  onChange={set("gdpr")}
                  className="gdpr-check mt-0.5"
                />
                <span className="text-sm text-slate-400 leading-relaxed">
                  {t.form.gdpr}{" "}
                  <Link
                    to="/adatkezeles"
                    data-testid="contact-gdpr-privacy-link"
                    className="text-cyan-300 underline underline-offset-4 hover:text-cyan-200 transition-colors duration-200"
                  >
                    {t.form.gdprLink}
                  </Link>{" "}
                  *
                </span>
              </label>
              <button
                data-testid="contact-form-submit-button"
                type="submit"
                disabled={sending}
                className="vortex-btn sm:col-span-2 inline-flex min-h-14 items-center justify-center gap-3 rounded-full text-base font-bold text-white disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <LoaderCircle className="w-5 h-5 animate-spin" />
                    {t.form.sending}
                  </>
                ) : (
                  <>
                    {t.form.submit}
                    <Send className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
